import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "../../../app/components/ui/button";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "../../../app/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../../app/components/ui/popover";
import { cn } from "../../../app/components/ui/utils";
import { api } from "../../../lib/api";
import { formatInrTrimmed } from "../../../lib/currency";
import { useDebouncedValue } from "../../../lib/useDebouncedValue";
import { useAuth } from "../../../providers/AuthProvider";

export type ProductSearchOption = { id: number; name: string; sku: string | null };

export function ProductSearchDropdown({
  value,
  onChange,
  placeholder = "Search product…",
  disabled,
  className,
}: {
  value: ProductSearchOption | null;
  onChange: (next: ProductSearchOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 300);

  const enabled = Boolean(token && debounced.trim().length > 0 && open);

  const { data, isFetching } = useQuery({
    queryKey: ["products-search", debounced],
    enabled,
    queryFn: async () => {
      if (!token) throw new Error("Missing auth token");
      const res = await api.products.search({ token, search: debounced });
      return res.data;
    },
    staleTime: 60_000,
  });

  const options: ProductSearchOption[] = useMemo(() => {
    return Array.isArray(data) ? (data as ProductSearchOption[]) : [];
  }, [data]);

  const pricingEnabled = Boolean(token && open && options.length > 0);
  const { data: pricingById } = useQuery({
    queryKey: ["posPricing-inline", options.map((o) => o.id).join(","), open],
    enabled: pricingEnabled,
    queryFn: async () => {
      if (!token) throw new Error("Missing auth token");
      const res = await api.posPricing.getBatchPricing({ token, ids: options.map((o) => o.id) });
      const out: Record<number, { unit_price: number | null }> = {};
      for (const [pid, row] of Object.entries(res.data)) {
        const unit = row.unit_sell_price != null ? Number.parseFloat(String(row.unit_sell_price)) : Number.NaN;
        out[Number(pid)] = { unit_price: Number.isFinite(unit) ? unit : null };
      }
      return out;
    },
    staleTime: 30_000,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn("w-full justify-between rounded-xl shadow-sm", className)}
        >
          <span className="truncate text-left">
            {value ? `${value.name}${value.sku ? ` (${value.sku})` : ""}` : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 size-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[420px] max-w-[calc(100vw-2rem)]" align="start">
        <Command>
          <CommandInput
            placeholder="Type to search products…"
            value={query}
            onValueChange={(v) => setQuery(v)}
          />
          <CommandList>
            <CommandEmpty>
              {isFetching ? "Searching…" : debounced.trim() ? "No products found." : "Type to search."}
            </CommandEmpty>
            {options.map((opt) => {
              const selected = value?.id === opt.id;
              const unit = pricingById?.[opt.id]?.unit_price ?? null;
              return (
                <CommandItem
                  key={opt.id}
                  value={`${opt.name} ${opt.sku ?? ""}`.trim()}
                  onSelect={() => {
                    onChange(selected ? null : opt);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("size-4", selected ? "opacity-100" : "opacity-0")} />
                  <div className="min-w-0 flex-1 flex items-center justify-between gap-2">
                    <span className="truncate">
                      {opt.name}
                      {opt.sku ? <span className="text-muted-foreground"> ({opt.sku})</span> : null}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {unit != null ? formatInrTrimmed(unit) : "—"}
                    </span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

